import React, { useCallback, useEffect, useMemo, useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { motion } from "framer-motion";
import { useTranslation } from "react-i18next";
import {
  FaArrowLeft,
  FaBell,
  FaCar,
  FaGift,
  FaPlane,
  FaStore,
  FaTicketAlt,
  FaWifi,
} from "react-icons/fa";

import Header from "../components/Header";
import Footer from "../components/Footer";
import SeoHead from "../components/SeoHead";
import TravelpayoutsWidget from "../components/travelComparator/TravelpayoutsWidget";
import AffiliateLaunchCard from "../components/travelComparator/AffiliateLaunchCard";
import CommercialConversionPanel from "../components/travelComparator/CommercialConversionPanel";
import ForfyTripPlanner, { type ForfyTripPlan, readSavedTripPlan } from "../components/travelComparator/ForfyTripPlanner";
import {
  getTravelWidget,
  TRAVEL_WIDGETS,
} from "../config/travelpayoutsWidgets";
import {
  rememberComparatorCategory,
  trackComparatorEvent,
} from "../lib/comparatorAnalytics";

const icons = {
  flights: FaPlane,
  cars: FaCar,
  esim: FaWifi,
  experiences: FaTicketAlt,
};

const localSuggestions = [
  {
    id: "forfeo-experiences",
    icon: FaGift,
    titleFr: "Expériences locales Forfeo",
    titleEn: "Forfeo local experiences",
    descriptionFr: "Ajoutez une expérience locale mémorable à votre séjour.",
    descriptionEn: "Add a memorable local experience to your stay.",
    path: "/boutique",
  },
  {
    id: "forfeo-last-call",
    icon: FaStore,
    titleFr: "Occasions Last Call",
    titleEn: "Last Call opportunities",
    descriptionFr:
      "Découvrez les disponibilités et occasions de dernière minute.",
    descriptionEn: "Discover last-minute availability and opportunities.",
    path: "/last-call",
  },
  {
    id: "forfeo-alerts",
    icon: FaBell,
    titleFr: "Alertes intelligentes",
    titleEn: "Smart alerts",
    descriptionFr:
      "Demandez à Forfy de surveiller les prochaines occasions pour vous.",
    descriptionEn: "Ask Forfy to watch for the next opportunities for you.",
    path: "/alertes-intelligentes",
  },
];

export default function TravelComparator() {
  const navigate = useNavigate();
  const [params, setParams] = useSearchParams();
  const { i18n } = useTranslation();
  const isEn = String(i18n.language || "")
    .toLowerCase()
    .startsWith("en");
  const active = useMemo(
    () => getTravelWidget(params.get("category") || undefined),
    [params],
  );

  const [tripPlan, setTripPlan] = useState<ForfyTripPlan>(() => readSavedTripPlan() || {
    destination: params.get("destination") || "",
    startDate: params.get("start") || "",
    endDate: params.get("end") || "",
    profile: (params.get("profile") as ForfyTripPlan["profile"]) || "couple",
    budget: params.get("budget") || "",
  });

  const updateTripPlan = useCallback((plan: ForfyTripPlan) => {
    setTripPlan(plan);
  }, []);

  useEffect(() => {
    rememberComparatorCategory(active.category);
    trackComparatorEvent("forfeo_comparator_view", {
      category: active.category,
    });
  }, [active.category]);

  const selectCategory = (category: string) => {
    rememberComparatorCategory(category);
    trackComparatorEvent("forfeo_comparator_category_select", { category });
    const next = new URLSearchParams(params);
    next.set("category", category);
    setParams(next);
  };

  const openSuggestion = (id: string, path: string) => {
    trackComparatorEvent("forfeo_comparator_local_suggestion_click", {
      category: active.category,
      suggestion: id,
    });
    navigate(path);
  };

  return (
    <div className="min-h-screen bg-slate-50 text-slate-900">
      <Header />
      <SeoHead
        title={
          isEn
            ? "Forfy Compare | Forfeo Explore"
            : "Forfy Compare | Comparateur voyage Forfeo"
        }
        description={
          isEn
            ? "Compare travel essentials with Forfy and Travelpayouts affiliate partners."
            : "Comparez les essentiels du voyage avec Forfy et les partenaires affiliés Travelpayouts."
        }
        canonicalPath="/comparateur-voyage"
      />

      <main className="pt-24 md:pt-28">
        <section className="relative overflow-hidden bg-slate-950 px-4 pb-14 pt-10 md:pb-20 md:pt-14">
          <div className="absolute inset-0 bg-[radial-gradient(circle_at_15%_20%,rgba(34,211,238,0.18),transparent_30%),radial-gradient(circle_at_85%_20%,rgba(59,130,246,0.2),transparent_32%)]" />
          <div className="container relative mx-auto">
            <button
              onClick={() => navigate("/")}
              className="mb-8 inline-flex items-center gap-2 rounded-full border border-white/10 bg-white/5 px-4 py-2 text-xs font-black uppercase tracking-widest text-slate-300 transition hover:bg-white/10 hover:text-white"
            >
              <FaArrowLeft /> {isEn ? "Home" : "Accueil"}
            </button>

            <div className="grid items-center gap-10 lg:grid-cols-[1.15fr_0.85fr]">
              <div>
                <div className="mb-5 inline-flex rounded-full border border-cyan-300/20 bg-white/10 px-4 py-2 text-[10px] font-black uppercase tracking-[0.22em] text-cyan-300">
                  Forfy Compare
                </div>
                <h1 className="mb-5 text-4xl font-black leading-[0.94] tracking-tighter text-white md:text-7xl">
                  {isEn
                    ? "Build your trip, one smart choice at a time."
                    : "Construisez votre voyage, un choix intelligent à la fois."}
                </h1>
                <p className="max-w-2xl text-lg font-medium leading-relaxed text-slate-300">
                  {isEn
                    ? "Select a category, compare partner options and complete your stay with local Forfeo experiences."
                    : "Sélectionnez une catégorie, comparez les options partenaires et complétez votre séjour avec les expériences locales Forfeo."}
                </p>
              </div>

              <motion.div
                animate={{ y: [0, -8, 0] }}
                transition={{ duration: 5, repeat: Infinity }}
                className="mx-auto w-full max-w-[380px] overflow-hidden rounded-[2.5rem] border border-white/15 bg-white/10 p-3 shadow-2xl backdrop-blur-xl"
              >
                <video
                  src="/GifForfy.MP4"
                  autoPlay
                  muted
                  loop
                  playsInline
                  className="h-[340px] w-full rounded-[2rem] object-cover"
                />
              </motion.div>
            </div>
          </div>
        </section>

        <section className="px-4 py-10 md:py-14">
          <div className="container mx-auto">
            <ForfyTripPlanner
              isEn={isEn}
              activeCategory={active.category}
              initialPlan={tripPlan}
              onPlanChange={updateTripPlan}
              onContinue={(category, plan) => {
                trackComparatorEvent("forfeo_comparator_plan_continue", {
                  category,
                  destination: plan.destination,
                  profile: plan.profile,
                  has_dates: Boolean(plan.startDate && plan.endDate),
                  has_budget: Boolean(plan.budget),
                });
                const next = new URLSearchParams(params);
                next.set("category", category);
                if (plan.destination) next.set("destination", plan.destination);
                if (plan.startDate) next.set("start", plan.startDate);
                if (plan.endDate) next.set("end", plan.endDate);
                next.set("profile", plan.profile);
                if (plan.budget) next.set("budget", plan.budget);
                setParams(next);
              }}
              onCreateAlert={(plan) => {
                trackComparatorEvent("forfeo_comparator_alert_click", {
                  category: active.category,
                  placement: "trip_planner",
                  destination: plan.destination,
                  profile: plan.profile,
                });
                const alertParams = new URLSearchParams({
                  source: "forfy_compare",
                  category: active.category,
                });
                if (plan.destination) alertParams.set("destination", plan.destination);
                if (plan.startDate) alertParams.set("start", plan.startDate);
                if (plan.endDate) alertParams.set("end", plan.endDate);
                if (plan.budget) alertParams.set("budget", plan.budget);
                navigate(`/alertes-intelligentes?${alertParams.toString()}`);
              }}
            />
            <div className="mb-8 flex gap-3 overflow-x-auto pb-2 hide-scrollbar">
              {TRAVEL_WIDGETS.map((widget) => {
                const Icon = icons[widget.category];
                const selected = widget.category === active.category;

                return (
                  <button
                    key={widget.id}
                    onClick={() => selectCategory(widget.category)}
                    className={`flex shrink-0 items-center gap-2 rounded-full px-5 py-3 text-xs font-black uppercase tracking-wider transition ${
                      selected
                        ? "bg-slate-950 text-white shadow-xl"
                        : "border border-slate-200 bg-white text-slate-600 hover:border-cyan-400"
                    }`}
                  >
                    <Icon
                      className={selected ? "text-cyan-300" : "text-cyan-600"}
                    />
                    {isEn ? widget.titleEn : widget.titleFr}
                  </button>
                );
              })}
            </div>

            <div className="grid items-start gap-8 lg:grid-cols-[0.72fr_1.28fr]">
              <aside className="rounded-[2rem] bg-gradient-to-br from-cyan-500 to-blue-700 p-7 text-white shadow-2xl">
                <p className="mb-3 text-[10px] font-black uppercase tracking-[0.24em] text-cyan-100">
                  {isEn ? "Forfy recommends" : "Forfy recommande"}
                </p>
                <h2 className="mb-4 text-3xl font-black tracking-tight">
                  {isEn ? active.titleEn : active.titleFr}
                </h2>
                <p className="mb-6 leading-relaxed text-white/85">
                  {isEn ? active.descriptionEn : active.descriptionFr}
                </p>
                <div className="rounded-2xl border border-white/15 bg-white/10 p-4 text-sm leading-relaxed text-white/80">
                  {isEn
                    ? "Partner results complement Forfeo local experiences. Final prices and booking conditions are confirmed on the partner website."
                    : "Les résultats partenaires complètent les expériences locales Forfeo. Les prix finaux et conditions de réservation sont confirmés sur le site partenaire."}
                </div>
                <button
                  onClick={() =>
                    openSuggestion("forfeo-alerts", "/alertes-intelligentes")
                  }
                  className="mt-6 inline-flex items-center gap-2 rounded-full bg-white px-5 py-3 text-xs font-black uppercase tracking-wider text-blue-700"
                >
                  <FaBell /> {isEn ? "Create an alert" : "Créer une alerte"}
                </button>
              </aside>

              {active.mode === "launch" ? (
                <AffiliateLaunchCard
                  isEn={isEn}
                  onLaunch={() => {
                    trackComparatorEvent("forfeo_comparator_partner_launch", {
                      category: active.category,
                    });
                    navigate(
                      active.launchPath || "/explorer-plus?category=autos",
                    );
                  }}
                />
              ) : active.scriptUrl ? (
                <TravelpayoutsWidget
                  src={active.scriptUrl}
                  title={isEn ? active.titleEn : active.titleFr}
                  category={active.category}
                  minHeight={active.minHeight}
                  onReady={() =>
                    trackComparatorEvent("forfeo_comparator_widget_ready", {
                      category: active.category,
                    })
                  }
                />
              ) : null}
            </div>

            <CommercialConversionPanel
              activeCategory={active.category}
              isEn={isEn}
              onAlert={() => {
                trackComparatorEvent("forfeo_comparator_alert_click", {
                  category: active.category,
                  placement: "commercial_panel",
                });
                navigate(
                  `/alertes-intelligentes?source=forfy_compare&category=${active.category}`,
                );
              }}
              onAction={(action) => {
                trackComparatorEvent("forfeo_comparator_cross_sell_click", {
                  category: active.category,
                  action: action.id,
                  destination: action.path,
                });
                navigate(action.path);
              }}
            />

            <section className="mt-12">
              <div className="mb-6 flex flex-col gap-2 md:flex-row md:items-end md:justify-between">
                <div>
                  <p className="text-[10px] font-black uppercase tracking-[0.22em] text-cyan-700">
                    Forfeo local
                  </p>
                  <h2 className="mt-2 text-3xl font-black tracking-tight text-slate-950">
                    {isEn
                      ? "Complete your trip with Forfeo"
                      : "Complétez votre voyage avec Forfeo"}
                  </h2>
                </div>
                <p className="max-w-xl text-sm leading-relaxed text-slate-500">
                  {isEn
                    ? "Partner tools help with travel logistics. Forfeo keeps local experiences and last-minute opportunities close at hand."
                    : "Les outils partenaires facilitent la logistique du voyage. Forfeo garde les expériences locales et occasions de dernière minute à portée de main."}
                </p>
              </div>

              <div className="grid gap-5 md:grid-cols-3">
                {localSuggestions.map(
                  ({
                    id,
                    icon: Icon,
                    titleFr,
                    titleEn,
                    descriptionFr,
                    descriptionEn,
                    path,
                  }) => (
                    <button
                      key={id}
                      onClick={() => openSuggestion(id, path)}
                      className="group rounded-[1.75rem] border border-slate-200 bg-white p-6 text-left shadow-sm transition hover:-translate-y-1 hover:border-cyan-300 hover:shadow-xl"
                    >
                      <span className="mb-5 flex h-12 w-12 items-center justify-center rounded-2xl bg-cyan-50 text-cyan-700 transition group-hover:bg-slate-950 group-hover:text-cyan-300">
                        <Icon />
                      </span>
                      <h3 className="text-lg font-black text-slate-950">
                        {isEn ? titleEn : titleFr}
                      </h3>
                      <p className="mt-2 text-sm leading-relaxed text-slate-500">
                        {isEn ? descriptionEn : descriptionFr}
                      </p>
                    </button>
                  ),
                )}
              </div>
            </section>

            <div className="mt-10 rounded-[2rem] border border-slate-200 bg-white p-6 text-sm leading-relaxed text-slate-500 shadow-sm">
              <strong className="text-slate-800">
                {isEn ? "Transparency:" : "Transparence :"}
              </strong>{" "}
              {isEn
                ? "Some tools and links are affiliate links. Forfeo may receive a commission when an eligible booking is made, at no additional cost to you."
                : "Certains outils et liens sont affiliés. Forfeo peut recevoir une commission lorsqu’une réservation admissible est effectuée, sans frais additionnels pour vous."}
            </div>
          </div>
        </section>
      </main>
      <Footer />
    </div>
  );
}
